// libraries
import { useContext, useCallback, useEffect, useState, useRef, useMemo } from 'react'
import Big from 'big.js'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import printJS from 'print-js'
import { v4 as uuidv4 } from 'uuid'
import {
  Box,
  Button,
  Typography,
  Stack,
  InputAdornment,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import {
  ManageSearch as ManageSearchIcon,
  AttachMoney as AttachMoneyIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Save as SaveIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Lock as LockIcon,
} from '@mui/icons-material'
import { GridApiRef } from '@mui/x-data-grid-pro'

// state
import { showFormModal, setSnackBar } from 'state/ui'
import { setConsumerInfo, clearConsumerInformation, clearOutboundTicketId } from 'state/outboundTicket'
import { setSalesOrderId, clearSalesOrderId, setSalesOrderFilter, clearSalesOrderFilters } from 'state/salesOrder'
import { clearInboundTicketId } from 'state/inboundTicket'

// specific components
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import InvoiceTicketTable from 'components/containers/outbound-tickets/components/InvoiceTicketTable'
import InvoicePaymentTableV2 from 'components/containers/outbound-tickets/components/InvoicePaymentTableV2'
import InvoiceFooter from 'components/containers/outbound-tickets/components/InvoiceFooter'

// generic components
import AdjustmentModal from 'components/generic/modals/dialog-modal/AdjustmentModal'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import DatePicker from 'components/generic/form/DatePicker'
import Switch from 'components/generic/form/Switch'
import { ACTION_TYPE } from 'components/generic/buttons/ButtonList'
import OutboundTicketsModal from 'components/generic/modals/dialog-modal/OutboundTicketsModal/OutboundTicketsModal'
import { Chip, SelectChip } from 'components/generic/chip'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import AlertSnackbar from 'components/generic/snackbars'
import EmailModal from 'components/generic/modals/dialog-modal/EmailModal'

// queries and mutations
import {
  useGetInvoiceV3Query,
  useCreateInvoiceV2Mutation,
  useUpdateInvoiceV2Mutation,
  useGetInvoicesV2Query,
  useVoidInvoiceMutation,
  useCreateInboundInvoiceV2Mutation,
  useUpdateInboundInvoiceV2Mutation,
} from 'services/invoices'
import {
  useGetOutboundTicketsQuery,
  useGetOutboundTicketQuery,
  useGetOutboundTicketsByIdsMutation,
} from 'services/outboundTickets'
import { useGetSalesOrderQuery } from 'services/salesOrders'
import { useGetCustomerQuery } from 'services/customers'
import { useGetInboundTicketQuery } from 'services/inboundTickets'
import { useGetBillQuery } from 'services/bills'
import { useGetPurchaseOrderQuery } from 'services/purchaseOrders'

// helpers & utils
import { request } from 'customRequest'
import { addToDate, getTotalWeightDeduction } from 'utils/data-manipulation/common'
import { dateFormatter, capitalize, dateTimeFormatter, roundTo } from 'utils/data-manipulation/valueFormatter'
import { dispatchSnackbar, setDecimalPrecision, checkIfDateIsLocked, generateLockTooltip } from 'utils/helpers'
import { trackEvent } from 'utils/tracking'
import {
  ALL_PAYMENT_METHOD_OPTIONS,
  WEIGHT_OPTIONS,
  CHURN_ZERO_EVENTS as cz,
  CURRENCY_OPTIONS,
  INVOICE_WEIGHT_MODES,
  ROUNDING_PRECISIONS,
  INVOICE_STATUSES,
  ENTITY_IN_LOCK_PERIOD_MESSAGE,
  SORTABLE_OBT_FIELDS,
  OUTBOUND_TICKET_STATUSES,
  PDF_TYPE,
  TICKET_SOURCE_TYPE,
} from 'utils/constants'
import { formOptions } from 'utils/validation/invoice'
import {
  initialInvoiceInformation,
  initialInvoiceSummaryRowData,
  invoiceStatusArray,
} from 'components/containers/outbound-tickets/utils'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import useQzTray from 'utils/custom-hooks/useQzTray'
import InvoiceTicketTableV2 from './components/InvoiceTicketTableV2'
import { useFilteredPaymentMethods } from 'utils/custom-hooks/useFilteredPaymentMethods'

const InvoiceContainerV2 = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const initialRender = useRef(true)
  const context = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)

  // invoice data
  const { id: invoiceId } = useParams()
  const isUpdate = !!invoiceId

  // state variables
  const methods = useForm(formOptions)
  const { handleSubmit, clearErrors, control, formState, setValue, getValues, reset, setError, watch } = methods
  const { errors } = formState
  const { salesOrderId } = useAppSelector(state => state.salesOrder)
  const { outboundTicketId, consumerInfo } = useAppSelector(state => state.outboundTicket)
  const { inboundTicketId } = useAppSelector((state: any) => state.inboundTicket)
  const { defaultCurrency, settings, defaultYardUm } = useAppSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const { defaultYardTimezone } = useAppSelector(state => state.auth)
  const [openAddOutboundTickets, setOpenAddOutboundTickets] = useState(false)
  const [outboundTicketsList, setOutboundTicketsList] = useState<any[]>([])
  const [selectedOutboundTickets, setSelectedOutboundTickets] = useState<any[]>([])
  const [selectedInboundTickets, setSelectedInboundTickets] = useState<any[]>([])
  const [totalRowData, setTotalRowData] = useState({})
  const [summaryData, setSummaryData] = useState(initialInvoiceSummaryRowData)
  const [isEditMode, setIsEditMode] = useState(false)
  const [ticketsAreLoading, setTicketsAreLoading] = useState(false)
  const [totalAmountPaid, setTotalAmountPaid] = useState(0)
  const [isFetchingPdf, setIsFetchingPdf] = useState(false)
  const [weightMode, setWeightMode] = useState(INVOICE_WEIGHT_MODES.YARD)
  const [cachedMaterialTypesData, setCachedMaterialTypesData] = useState<{ [key: string]: any }>({})
  const { paymentTermsOptions, dueDateCalculation } = usePaymentTerms({})
  const [voidInvoiceErrorMsg, setVoidInvoiceErrorMsg] = useState('')
  const [isFetchingOutboundTickets, setIsFetchingOutboundTickets] = useState(isUpdate)
  const [apiRefDict, setApiRefDict] = useState<{ [key: string]: GridApiRef }>({})
  const { qzPrint } = useQzTray()
  const { hasAtm } = useAppSelector(state => state.settings)
  const weightModeRef = useRef(weightMode)
  const totalRowDataRef = useRef({})

  // Get yard's default payment method from settings (same as InboundTicketBillContainer)
  const yardPreferredPaymentMethod = settings?.purchaseSettings?.payment?.yardPreferredPaymentMethod

  useEffect(() => {
    weightModeRef.current = weightMode
  }, [weightMode])

  useEffect(() => {
    totalRowDataRef.current = totalRowData
  }, [totalRowData])

  const issueDate = watch('details.issueDate')
  const selectedPaymentTermId = watch('details.paymentTermId')
  const invoiceCurrency = watch('details.currency')
  const customForeignExchangeRateEnabled = watch('details.customForeignExchangeRateEnabled')
  const showCustomFxRate = invoiceCurrency !== defaultCurrency
  const { pricePrepend } = useGetPricePrepend({ selectedCurrency: invoiceCurrency })

  const [isInvoiceNumberValid, setIsInvoiceNumberValid] = useState(true)
  const [invoiceNumTouched, setInvoiceNumTouched] = useState(false)
  const [showVoidModal, setShowVoidModal] = useState(false)

  // Feature flags
  const {
    supportVoidInvoice,
    disableSavingInvoicesWithNoTickets,
    enablePdfEmails,
    enableLockAccountingV2,
    enableNegativeIbtToInvoice,
  } = useAppSelector(state => state.featureFlags?.variables || {})

  // queries and mutations
  const { data: invoiceData, isFetching: isFetchingInvoice } = useGetInvoiceV3Query({ invoiceId }, { skip: !isUpdate })

  const { data: salesOrderData, isFetching: isFetchingSalesOrderData } = useGetSalesOrderQuery(
    { salesOrderId },
    { skip: !salesOrderId },
  )

  const [createInvoice, { isLoading: isCreatingInvoice }] = useCreateInvoiceV2Mutation()
  const [updateInvoice, { isLoading: isUpdatingInvoice }] = useUpdateInvoiceV2Mutation()
  const [createInboundInvoice, { isLoading: isCreatingInboundInvoice }] = useCreateInboundInvoiceV2Mutation()
  const [updateInboundInvoice, { isLoading: isUpdatingInboundInvoice }] = useUpdateInboundInvoiceV2Mutation()

  const [getOutboundTicketsByIds] = useGetOutboundTicketsByIdsMutation()

  const { data: outboundTicketData, isFetching: isFetchingOutboundTicketData } = useGetOutboundTicketQuery(
    { outboundTicketId },
    { skip: !outboundTicketId },
  )

  const { data: inboundTicketData, isFetching: isFetchingInboundTicketData } = useGetInboundTicketQuery(
    { ticketId: inboundTicketId },
    { skip: !inboundTicketId || !enableNegativeIbtToInvoice },
  )

  // Get bill data for inbound invoices to access bill tax rate
  const billId =
    enableNegativeIbtToInvoice && selectedInboundTickets.length > 0 ? selectedInboundTickets[0]?.billId : null
  const { data: billData, isFetching: isFetchingBillData } = useGetBillQuery({ billId }, { skip: !billId })

  // Get purchase order data for inbound tickets to access material override names
  const purchaseOrderId =
    enableNegativeIbtToInvoice && selectedInboundTickets.length > 0
      ? selectedInboundTickets[0]?.transportationInfo?.purchaseOrderId
      : null

  const { data: purchaseOrderData, isFetching: isFetchingPurchaseOrderData } = useGetPurchaseOrderQuery(
    { purchaseOrderId },
    { skip: !purchaseOrderId },
  )

  const [voidInvoice, { isLoading: isVoidingInvoice }] = useVoidInvoiceMutation()

  const priceSheetsData = useMemo(
    () => ({
      Items: [
        {
          entriesObj: Object.values(cachedMaterialTypesData).map(materialType => ({
            ...(materialType?.defaultPriceEntry || {}),
            commodityId: materialType.commodityId,
            commodityName: materialType.commodityName,
            materialCode: materialType.code,
            materialName: materialType.commonName,
            tareWeightRequired: materialType.tareWeightRequired,
          })),
        },
      ],
    }),
    [cachedMaterialTypesData],
  )

  // Get customer data - different sources for inbound vs outbound invoices
  const customerId = useMemo(() => {
    if (invoiceData) {
      return invoiceData.details.customer.id
    }
    if (salesOrderData) {
      return salesOrderData.customerId
    }
    if (selectedInboundTickets.length > 0) {
      return selectedInboundTickets[0]?.customer?.id
    }
    return null
  }, [invoiceData, salesOrderData, selectedInboundTickets])

  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId },
    { skip: !customerId },
  )

  // Get filtered payment methods for inbound tickets (customer-specific filtering)
  const inboundPaymentMethods = useFilteredPaymentMethods({
    hasAtm: hasAtm || false,
    customerGsPayContactId: customerData?.gsPayContactId,
    paymentMethods: [],
  })

  // Get filtered payment methods for outbound tickets (simple filtering)
  const outboundPaymentMethods = useFilteredPaymentMethods({
    hasAtm: hasAtm || false,
    simpleFilter: true,
  })

  // Choose the appropriate payment methods based on ticket type
  const PAYMENT_METHOD_OPTIONS = useMemo(() => {
    if (enableNegativeIbtToInvoice && selectedInboundTickets?.length > 0) {
      return inboundPaymentMethods
    } else {
      return outboundPaymentMethods
    }
  }, [enableNegativeIbtToInvoice, selectedInboundTickets?.length, inboundPaymentMethods, outboundPaymentMethods])

  const { data: outboundTicketsData, isFetching: isFetchingOutboundTicketsData } = useGetOutboundTicketsQuery(
    {
      page: 1,
      pageSize: 500,
      filter: JSON.stringify({
        sort: {
          field: SORTABLE_OBT_FIELDS.EFFECTIVE_DATE,
          ascending: false,
        },
        statuses: [
          OUTBOUND_TICKET_STATUSES.INCOMPLETE.id,
          OUTBOUND_TICKET_STATUSES.SHIPPED.id,
          OUTBOUND_TICKET_STATUSES.INVOICED.id,
        ],
        customerId: invoiceData?.details.customer.id ?? salesOrderData?.customerId,
      }),
    },
    { skip: !invoiceData && !salesOrderData },
  )

  const isLoading =
    isFetchingInvoice ||
    isFetchingSalesOrderData ||
    isFetchingOutboundTicketsData ||
    isFetchingOutboundTicketData ||
    isFetchingInboundTicketData ||
    isFetchingBillData ||
    isFetchingPurchaseOrderData ||
    isCreatingInvoice ||
    isUpdatingInvoice ||
    isCreatingInboundInvoice ||
    isLoadingCustomerData ||
    isVoidingInvoice ||
    isFetchingOutboundTickets ||
    isFetchingPdf

  useGlobalLoader(isLoading)

  // Calculate bill tax rate for inbound invoices (same pattern as InboundTicketBillContainer)
  const billTaxRate = useMemo(() => {
    if (!enableNegativeIbtToInvoice) return 0
    return (
      parseFloat(!billData || billData?.status?.toUpperCase() === 'DUE' ? customerData?.taxRate : billData?.taxRate) ||
      0
    )
  }, [enableNegativeIbtToInvoice, billData, customerData])

  const isInLockedAccountingPeriodV1 = useMemo(() => {
    if (enableLockAccountingV2) return false // if lock accounting V2 is turned on, we do not need to disable any fields
    if (!invoiceData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    if (!invoiceData.details.issueDate || !lockAccountingPeriod.lockAccountingDate) return false
    return (
      new Date(invoiceData.details.issueDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
    )
  }, [lockAccountingPeriod, invoiceData, enableLockAccountingV2])

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!invoiceData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false

    const hasLockedTickets = selectedOutboundTickets?.some(ticket =>
      checkIfDateIsLocked(lockAccountingPeriod, ticket.effectiveDate),
    )
    if (hasLockedTickets) return true

    return checkIfDateIsLocked(lockAccountingPeriod, invoiceData?.details?.issueDate)
  }, [lockAccountingPeriod, invoiceData, enableLockAccountingV2, selectedOutboundTickets])

  const handleOpenBulkPayment = paymentId => {
    navigate(`/app/sales/invoice-payments/${paymentId}`)
  }

  const handleSuccess = (id, type) => {
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: `Invoice #${id} ${type}`,
      }),
    )
  }

  const invoiceHasPayments = (invoiceData?.paymentSplits?.length ?? 0) > 0

  useEffect(
    // Reads the payment data from the invoice
    function readPaymentData() {
      if (invoiceData) {
        // Reads amount paid from the invoice
        setTotalAmountPaid(invoiceData.amountPaid)
      }
    },
    [invoiceData],
  )

  const onClickVoidInvoice = async () => {
    setShowVoidModal(false)
    if (invoiceData?.status?.toUpperCase() === INVOICE_STATUSES.VOID.id) {
      setVoidInvoiceErrorMsg('This invoice is already marked as VOID')
      return null
    }
    if (invoiceHasPayments) {
      setVoidInvoiceErrorMsg('An error occurred voiding the invoice. Please ensure outstanding payments are voided.')
      return null
    }
    await voidInvoice({
      invoiceId: (invoiceData as any).id,
    } as any)
      .unwrap()
      .then(() => {
        trackEvent(cz.VOID_INVOICE)
        navigate('/app/sales/invoices')
        dispatchSnackbar(dispatch, 'success', 'Invoice Voided')
      })
      .catch(error => {
        const errorMsg =
          error?.data?.error || error?.error || error?.message || 'An error occurred voiding the invoice.'
        dispatchSnackbar(dispatch, 'error', voidInvoiceErrorMsg ?? errorMsg)
        trackEvent({
          eventName: cz.VOID_INVOICE,
          errorMsg,
        })
        setVoidInvoiceErrorMsg(errorMsg)
      })
    return null
  }

  const handleError = (error, type) => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || `There was an error ${type} your invoice`,
      }),
    )
  }

  const handlePrint = async () => {
    if (!invoiceData?.id) {
      return
    }

    setIsFetchingPdf(true)
    const pdfUrl = `/core-api/sales/invoices/${invoiceData.id}/pdf/INVOICE`
    const documentTitle = `Invoice #${invoiceData.invoiceTicketId}`

    const onLoadingStart = () => {
      console.log('Loading PDF for printing...')
    }

    const onLoadingEnd = () => {
      console.log('PDF loaded successfully for printing.')
    }

    const onPdfOpen = () => {
      console.log('PDF opened successfully.')
    }

    const onPrintDialogClose = () => {
      console.log('Print dialog closed.')
    }

    const onError = error => {
      console.error('Error occurred while printing PDF:', error)
    }

    try {
      const response = await request(pdfUrl, 'get', {}, { accept: 'application/pdf' }, true)
      const documentPrinter = localStorage.getItem('documentPrinter')

      if (!documentPrinter || documentPrinter === 'none') {
        printJS({
          printable: response,
          type: 'pdf',
          base64: true,
          documentTitle,
          onLoadingStart,
          onLoadingEnd,
          onPdfOpen,
          onPrintDialogClose,
          onError,
        })
      } else {
        await qzPrint(response, documentPrinter).catch(() => {
          printJS({
            printable: response,
            type: 'pdf',
            base64: true,
            documentTitle,
          })
        })
      }
    } catch (error) {
      dispatch(
        setSnackBar({
          snackbarType: 'error',
          snackbarMessage: 'An error occurred while generating the invoice PDF',
        }),
      )
    } finally {
      setIsFetchingPdf(false)
    }
  }

  const fetchTickets = useCallback(
    async outboundTicketIds => {
      try {
        if (outboundTicketIds?.length > 0) {
          const { data: selectedOutboundTicketsData, error: getSelectedOutboundTicketsError } =
            await getOutboundTicketsByIds({
              body: outboundTicketIds,
            })
          if (getSelectedOutboundTicketsError) throw new Error(getSelectedOutboundTicketsError?.data?.error)
          setSelectedOutboundTickets(selectedOutboundTicketsData?.Items || [])
        } else if (disableSavingInvoicesWithNoTickets) {
          setSelectedOutboundTickets([])
        }
        setIsFetchingOutboundTickets(false)
      } catch (error) {
        setSelectedOutboundTickets([])
        setIsFetchingOutboundTickets(false)
        dispatch(
          setSnackBar({
            snackbarType: 'error',
            snackbarMessage: 'An error occurred while fetching the ticket data',
          }),
        )
      }
    },
    [disableSavingInvoicesWithNoTickets],
  )

  const formatConsumerWeights = () => {
    const formattedConsumerWeights: any[] = []
    const consumerWeights = getValues('consumerWeights')

    if (consumerWeights) {
      const outboundTicketIds = new Set(selectedOutboundTickets.map(({ id }) => id))
      const filteredConsumerWeights: any[][] = Object.entries(consumerWeights).filter(([id]) =>
        outboundTicketIds.has(id),
      )
      filteredConsumerWeights.forEach(([obtId, weights]) => {
        const consumerWeightsMaterials: any[] = Object.values(weights)
        consumerWeightsMaterials.forEach(weight => {
          const {
            id,
            consumerGross,
            consumerTare,
            consumerPrice,
            consumerUm,
            consumerItemCount,
            materialTypeId,
            orderEntryId,
            deductions,
          } = weight

          const originalNet = Number(consumerGross) - Number(consumerTare)
          const weightDeduction = getTotalWeightDeduction(deductions, originalNet)
          const consumerNetWeight = originalNet - weightDeduction

          formattedConsumerWeights.push({
            id: id || uuidv4(),
            outboundTicketId: obtId,
            materialTypeId,
            orderEntryId,
            materialNameOverride: weight.consumerMaterialName?.trim() || null,
            consumerItemCount: Number(consumerItemCount),
            consumerNetWeight,
            consumerGross: Number(consumerGross),
            consumerTare: Number(consumerTare),
            consumerPrice: Number(consumerPrice || 0),
            um: consumerUm || defaultYardUm,
            deductions,
          })
        })
      })
    }
    return formattedConsumerWeights
  }

  const formatData = data => {
    const consumerWeights = formatConsumerWeights()

    // Determine which tickets to use - prefer inbound tickets if present
    const selectedTickets = selectedInboundTickets.length > 0 ? selectedInboundTickets : selectedOutboundTickets
    const ticketIds = selectedTickets.map(ticket => ticket.id)

    return {
      id: invoiceData?.id,
      salesOrderId: salesOrderData?.id,
      invoiceTicketId: data?.details?.invoiceNo || null,
      status: {
        value: data?.status?.toUpperCase() || 'OPEN',
      },
      issueDate: Date.parse(data?.details?.issueDate),
      total: roundTo(summaryData.grossValue - summaryData.tareValue, ROUNDING_PRECISIONS.COST),
      outboundTickets: selectedOutboundTickets.map(outboundTicket => outboundTicket.id),
      inboundTickets: selectedInboundTickets.map(inboundTicket => inboundTicket.id),
      notes: data?.details?.notes,
      consumerWeights,
      usingConsumerWeights: weightModeRef.current !== INVOICE_WEIGHT_MODES.YARD,
      lockConsumerWeights: invoiceData?.lockConsumerWeights,
      customForeignExchangeRateEnabled: data?.details?.customForeignExchangeRateEnabled || false,
      foreignExchangeRate: data?.details?.foreignExchangeRate ? Number(data.details.foreignExchangeRate) : null,
    }
  }

  const onSubmit = async (data, type) => {
    const formattedData = formatData(data)

    const hasInboundTickets = selectedInboundTickets.length > 0
    const hasOutboundTickets = selectedOutboundTickets.length > 0

    if (hasInboundTickets && hasOutboundTickets) {
      handleError(
        { message: 'Cannot create invoice with both inbound and outbound tickets. Please select only one type.' },
        'validating',
      )
      return
    }

    if (!hasInboundTickets && !hasOutboundTickets) {
      handleError({ message: 'Please select at least one ticket to create an invoice.' }, 'validating')
      return
    }

    const isInboundInvoice = hasInboundTickets

    if (isInboundInvoice) {
      if (selectedInboundTickets.length > 1) {
        handleError(
          {
            message:
              'Multiple inbound tickets per invoice is not currently supported. Please select only one inbound ticket.',
          },
          'validating',
        )
        return
      }

      const inboundTicket = selectedInboundTickets[0]

      const inboundInvoiceData = {
        id: invoiceData?.id,
        status: formattedData.status,
        issueDate: formattedData.issueDate,
        invoiceTicketId: formattedData.invoiceTicketId,
        notes: formattedData.notes,
        customForeignExchangeRateEnabled: formattedData.customForeignExchangeRateEnabled,
        foreignExchangeRate: formattedData.foreignExchangeRate,

        // Inbound-specific fields
        billId: !isUpdate ? inboundTicket.billId : null, // no need to send bill id on update
        paymentTermId: data?.details?.paymentTermId ?? customerData?.defaultPaymentTermId ?? null,
        paymentMethod: data?.details?.paymentMethod ?? yardPreferredPaymentMethod ?? null,

        // Optional fields that may be added later
        total: summaryData?.totalValue || null,
      }

      if (isUpdate) {
        delete inboundInvoiceData.invoiceTicketId
        await updateInboundInvoice({
          invoiceId: (inboundInvoiceData as any).id,
          invoiceInboundRequestBody: inboundInvoiceData,
        })
          .unwrap()
          .then(updatedInvoice => {
            handleSuccess(formattedData.invoiceTicketId, 'updated')
            reset(data)
            trackEvent({
              eventName: cz.UPDATE_INVOICE,
              description: updatedInvoice?.yardSummary?.total,
              quantity: updatedInvoice?.tickets?.length,
            })
            navigate(`/app/sales/invoices/${formattedData.id}`)
          })
          .catch(error => {
            const errorMsg =
              error?.data?.error || error?.error || error?.message || 'An error occurred updating the invoice.'
            trackEvent({
              eventName: cz.UPDATE_INVOICE,
              errorMsg,
            })
            handleError(error, 'updating')
          })
      } else {
        await createInboundInvoice({ invoiceInboundRequestBody: inboundInvoiceData })
          .unwrap()
          .then(newInvoice => {
            if (type === 'generate') {
              const serializedSummaryData = summaryData
                ? {
                    ...summaryData,
                    taxValue: summaryData.taxValue?.toNumber ? summaryData.taxValue.toNumber() : summaryData.taxValue,
                    totalValue: summaryData.totalValue?.toNumber
                      ? summaryData.totalValue.toNumber()
                      : summaryData.totalValue,
                    subTotal: summaryData.subTotal?.toNumber ? summaryData.subTotal.toNumber() : summaryData.subTotal,
                  }
                : summaryData

              dispatch(
                showFormModal({
                  formModalName: 'invoice-confirmation',
                  formModalProps: {
                    invoiceId: newInvoice.invoiceTicketId,
                    invoiceData: newInvoice,
                    selectedInboundTickets,
                    customerData,
                    summaryData: serializedSummaryData,
                    totalAmountPaid,
                    pricePrepend,
                  },
                }),
              )
            } else {
              handleSuccess(newInvoice.invoiceTicketId, 'created')
            }
            trackEvent({
              eventName: cz.CREATE_INVOICE,
              description: summaryData?.totalValue,
              quantity: selectedInboundTickets?.length,
            })
            reset(data)
            navigate(`/app/sales/invoices/${newInvoice.id}`)
          })
          .catch(error => {
            const errorMsg =
              error?.data?.error || error?.error || error?.message || 'An error occurred creating the inbound invoice.'
            trackEvent({
              eventName: cz.CREATE_INVOICE,
              errorMsg,
            })
            handleError(error, 'creating')
          })
      }
    } else {
      if (isUpdate) {
        delete formattedData.invoiceTicketId
        await updateInvoice({
          invoiceId: (formattedData as any).id,
          body: formattedData,
        })
          .unwrap()
          .then(updatedInvoice => {
            handleSuccess(updatedInvoice.invoiceTicketId, 'updated')
            reset(data)
            trackEvent({
              eventName: cz.UPDATE_INVOICE,
              description: updatedInvoice?.total,
              quantity: updatedInvoice?.outboundTicketIds?.length,
            })
            navigate(`/app/sales/invoices/${formattedData.id}`)
          })
          .catch(error => {
            const errorMsg =
              error?.data?.error || error?.error || error?.message || 'An error occurred updating the invoice.'
            trackEvent({
              eventName: cz.UPDATE_INVOICE,
              errorMsg,
            })
            handleError(error, 'updating')
          })
      } else {
        // Handle outbound invoice creation
        await createInvoice({ invoiceRequestBody: formattedData })
          .unwrap()
          .then(newInvoice => {
            if (type === 'generate') {
              // Serialize Big2 objects to numbers to avoid Redux serialization errors
              const serializedSummaryData = summaryData
                ? {
                    ...summaryData,
                    taxValue: summaryData.taxValue?.toNumber ? summaryData.taxValue.toNumber() : summaryData.taxValue,
                    totalValue: summaryData.totalValue?.toNumber
                      ? summaryData.totalValue.toNumber()
                      : summaryData.totalValue,
                    subTotal: summaryData.subTotal?.toNumber ? summaryData.subTotal.toNumber() : summaryData.subTotal,
                  }
                : summaryData

              dispatch(
                showFormModal({
                  formModalName: 'invoice-confirmation',
                  formModalProps: {
                    invoiceId: newInvoice?.invoiceTicketId,
                    invoiceData: newInvoice,
                    selectedOutboundTickets,
                    salesOrderData,
                    customerData,
                    priceSheetsData,
                    priceSheet: salesOrderData?.priceSheet,
                    summaryData: serializedSummaryData,
                    totalAmountPaid,
                    pricePrepend,
                  },
                }),
              )
            } else {
              handleSuccess(newInvoice.invoiceTicketId, 'created')
            }
            trackEvent({
              eventName: cz.CREATE_INVOICE,
              description: newInvoice?.total,
              quantity: newInvoice?.outboundTicketIds?.length,
            })
            reset(data)
            navigate(`/app/sales/invoices/${newInvoice.id}`)
          })
          .catch(error => {
            const errorMsg =
              error?.data?.error || error?.error || error?.message || 'An error occurred creating the invoice.'
            trackEvent({
              eventName: cz.CREATE_INVOICE,
              errorMsg,
            })
            handleError(error, 'creating')
          })
      }
    }
  }

  const onGenerate = async data => {
    await onSubmit(data, 'generate')
  }

  const onSave = async data => {
    await onSubmit(data, 'save')
  }

  // page setup
  useEffect(() => {
    dispatch(clearConsumerInformation())
    dispatch(clearSalesOrderId())
    dispatch(clearSalesOrderFilters())
    dispatch(clearOutboundTicketId())
    // Only clear inboundTicketId if we're updating an existing invoice
    // For new invoices, keep the inboundTicketId so it can be used to fetch ticket data
    if (isUpdate) {
      dispatch(clearInboundTicketId())
    }
    tabsContext.setContainerTabsVisibility(false)
  }, [])

  useEffect(() => {
    if (invoiceData && !isEditMode) {
      // For inbound invoices, set selected inbound tickets from invoice data
      if (invoiceData.sourceType === 'INBOUND') {
        const inboundTickets = invoiceData.tickets || []
        setSelectedInboundTickets(inboundTickets)
        setIsFetchingOutboundTickets(false) // No need to fetch outbound tickets for inbound invoices
      } else {
        // For outbound invoices, set sales order ID and fetch outbound ticket details
        if (invoiceData.details.salesOrderId) {
          dispatch(setSalesOrderId(invoiceData.details.salesOrderId))
        }
        setIsFetchingOutboundTickets(true)
        const outboundTicketIds = invoiceData.tickets?.map(ticket => ticket.id) || []
        fetchTickets(outboundTicketIds)
      }
    }
  }, [invoiceData, isEditMode])

  useEffect(() => {
    if (salesOrderData && !isEditMode && !isUpdate) {
      dispatch(
        setConsumerInfo({
          salesOrder: salesOrderData.salesOrderTicketId,
          name: {
            id: salesOrderData.customer?.id,
            customerCommonIdentifierString: salesOrderData.customer?.companyName,
          },
        }),
      )
    }
  }, [salesOrderData, isEditMode])

  const getMoreInvoiceActions = () => [
    {
      id: 'void-invoice-action',
      title: 'Void Invoice',
      action: 'Void Invoice',
      // TODO: removing feature flag but consider what to do with this
      disabled: !supportVoidInvoice || isInLockedAccountingPeriodV1,
      onClick: () => setShowVoidModal(true),
    },
  ]

  const getInvoiceStatus = () => {
    const fallBack = INVOICE_STATUSES.OPEN.name
    if (!invoiceData) return fallBack

    const statusToFind = invoiceData.status.toUpperCase()
    if (!statusToFind) return fallBack

    if (INVOICE_STATUSES[statusToFind]) {
      return INVOICE_STATUSES[statusToFind].name
    }
    dispatchSnackbar(dispatch, 'error', 'Invalid invoice status detected.')
  }

  useEffect(() => {
    if (invoiceData) {
      setValue('status', invoiceData.status?.toUpperCase() || INVOICE_STATUSES.OPEN.id)
      context.setContainerTitle(
        <Stack direction="row" alignItems="center" spacing={1}>
          {isInLockedAccountingPeriodV2 && (
            <Tooltip title={generateLockTooltip('invoice')} placement="top">
              <LockIcon color="info" />
            </Tooltip>
          )}
          <Typography variant="h5">Invoice #{invoiceData.invoiceTicketId}</Typography>
          {isEditMode ? (
            <SelectChip control={control} items={invoiceStatusArray} />
          ) : (
            <Chip label={getInvoiceStatus()} />
          )}
        </Stack>,
      )
    } else {
      context.setContainerTitle('New Invoice')
    }
  }, [invoiceData, isEditMode])

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)

  const getBalance = () => {
    if (isUpdate && invoiceData && !isEditMode) {
      return invoiceData.balance
    } else if (!isLoading && !ticketsAreLoading && summaryData.grossWeight) {
      return summaryData.totalValue - totalAmountPaid
    } else {
      return 0
    }
  }

  const [openDeduction, setOpenDeduction] = useState(false)
  const [onSaveDeductionHandler, setOnSaveDeductionHandlerState] = useState(() => deductions => {})
  const [selectedDeductions, setSelectedDeductions] = useState([])

  // Sets the header display, including invoice number, status, balance, and buttons
  useEffect(
    function setHeader() {
      const hasTickets = selectedInboundTickets.length > 0 || selectedOutboundTickets.length > 0

      const generateInvoiceAction = {
        text: 'GENERATE INVOICE',
        id: 'generate-invoice',
        disabled: isLoading || ticketsAreLoading || !hasTickets || !isInvoiceNumberValid,
        onClick: handleSubmit(onGenerate),
      }
      const remainingBalance = getBalance()
      const balanceTextAction = {
        text: (
          <Stack spacing={0.5} direction="row" alignItems="end">
            <Typography sx={{ fontSize: '14px' }}>{'Balance: '}</Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '16px',
              }}
            >
              {`${pricePrepend}${setDecimalPrecision(remainingBalance, { decimalPrecisionOverride: 2 })}`}
            </Typography>
          </Stack>
        ),
        id: 'balance',
        disabled: true,
        variant: 'text',
        onClick: () => {},
        sx: {
          textTransform: 'none',
        },
      }
      const saveAction = {
        text: 'SAVE',
        icon: isUpdate ? <SaveIcon /> : null,
        id: isUpdate ? 'update-invoice' : 'save-invoice',
        disabled:
          isLoading ||
          ticketsAreLoading ||
          !isInvoiceNumberValid ||
          (disableSavingInvoicesWithNoTickets && !hasTickets),
        variant: isUpdate ? 'contained' : 'outlined',
        onClick: handleSubmit(onSave),
      }
      const cancelAction = {
        text: 'CANCEL',
        id: isUpdate ? 'cancel-edit-invoice' : 'cancel-invoice',
        disabled: isLoading || ticketsAreLoading,
        variant: isUpdate ? 'outlined' : 'text',
        onClick: () => {
          if (isUpdate) {
            setIsEditMode(false)
            setWeightMode(invoiceData?.usingConsumerWeights ? INVOICE_WEIGHT_MODES.CONSUMER : INVOICE_WEIGHT_MODES.YARD)
          } else {
            navigate('/app/sales/invoices')
          }
        },
      }
      const recordPaymentAction = {
        text: 'RECORD A PAYMENT',
        icon: <AttachMoneyIcon />,
        id: 'record-payment',
        disabled: isLoading || ticketsAreLoading,
        onClick: () =>
          dispatch(
            showFormModal({
              formModalName: 'invoice-payment',
              formModalProps: {
                invoiceId: invoiceData?.id,
                invoiceNumber: invoiceData?.invoiceTicketId,
                summaryData,
                paymentMethod:
                  selectedInboundTickets.length > 0
                    ? getValues('details.paymentMethod') || customerData?.preferredPaymentMethod?.toLowerCase() || ''
                    : salesOrderData?.paymentMethod || '',
                totalAmountPaid,
                pricePrepend,
              },
            }),
          ),
      }
      const editAction = {
        text: 'EDIT',
        icon: <EditIcon />,
        id: 'edit-invoice',
        disabled: isLoading || ticketsAreLoading || isInLockedAccountingPeriodV1,
        variant: 'text',
        onClick: () => setIsEditMode(true),
        type: ACTION_TYPE.TooltipButton,
        tooltipProps: {
          title: isInLockedAccountingPeriodV1 ? ENTITY_IN_LOCK_PERIOD_MESSAGE : '',
        },
      }

      const printAction = {
        text: 'PRINT',
        icon: <PrintIcon />,
        id: 'print-invoice-action',
        disabled: isLoading || ticketsAreLoading,
        variant: 'text',
        onClick: handlePrint,
      }

      const emailAction = {
        text: 'EMAIL',
        icon: <EmailIcon />,
        id: 'email-invoice',
        disabled: isLoading || ticketsAreLoading,
        variant: 'text',
        onClick: () => setIsEmailModalOpen(true),
        type: ACTION_TYPE.TooltipButton,
        tooltipProps: {
          title: 'Email Invoice',
        },
      }

      const moreActionsButton = {
        text: '',
        variant: 'text',
        id: 'more-actions-button',
        disabled: isLoading || ticketsAreLoading,
        onClick: option => option.onClick(),
        type: ACTION_TYPE.SelectButton,
        endIcon: <MoreVertIcon />,
        options: getMoreInvoiceActions(),
      }

      if (!isUpdate) {
        context.setContainerActions([generateInvoiceAction, saveAction, cancelAction])
      } else if (isEditMode) {
        context.setContainerActions([balanceTextAction, saveAction, cancelAction])
      } else if (invoiceData?.status?.toUpperCase() === INVOICE_STATUSES.PAID.id) {
        context.setContainerActions([
          balanceTextAction,
          printAction,
          ...(enablePdfEmails ? [emailAction] : []),
          moreActionsButton,
        ])
      } else if (invoiceData?.status?.toUpperCase() === INVOICE_STATUSES.VOID.id) {
        context.setContainerActions([balanceTextAction, printAction, ...(enablePdfEmails ? [emailAction] : [])])
      } else {
        context.setContainerActions([
          balanceTextAction,
          recordPaymentAction,
          editAction,
          printAction,
          ...(enablePdfEmails ? [emailAction] : []),
          moreActionsButton,
        ])
      }
    },
    [
      invoiceData,
      isLoading,
      ticketsAreLoading,
      selectedOutboundTickets,
      selectedInboundTickets,
      isEditMode,
      summaryData,
      totalAmountPaid,
      isInvoiceNumberValid,
      pricePrepend,
      disableSavingInvoicesWithNoTickets,
      isInLockedAccountingPeriodV1,
    ],
  )

  useEffect(() => {
    // setup initial form data
    if (isUpdate && invoiceData) {
      // Viewing an existing invoice - handle both inbound and outbound
      const isInboundInvoice = invoiceData.sourceType === 'INBOUND'
      const customerInfo = {
        name: {
          id: invoiceData.details.customer.id,
          customerCommonIdentifierString: invoiceData.details.customer.displayName || '',
        },
        salesOrder: isInboundInvoice ? '' : invoiceData.details.salesOrderNumber || '',
      }
      dispatch(setConsumerInfo(customerInfo))
      reset({
        customer: customerInfo,
        details: {
          invoiceNo: invoiceData.invoiceTicketId || '',
          invoiceWeights: invoiceData.usingConsumerWeights ? WEIGHT_OPTIONS[1].id : WEIGHT_OPTIONS[0].id,
          paymentMethod: invoiceData.details.defaultPaymentMethod || '',
          paymentTermId: invoiceData.details.paymentTermId || '',
          currency: invoiceData.currency || defaultCurrency || '',
          issueDate: invoiceData.details.issueDate || new Date(),
          dateDue: invoiceData.details.dueDate || new Date(),
          notes: invoiceData.notes || '',
          customForeignExchangeRateEnabled: invoiceData.customForeignExchangeRateEnabled || false,
          foreignExchangeRate: invoiceData.foreignExchangeRate || null,
        },
        // reset consumerWeights with itself to prevent it from getting cleared if this useEffect reruns (eg. due to a page error)
        consumerWeights: getValues('consumerWeights') || [],
      })
    } else if (salesOrderData) {
      // Populating sales order data when creating a new invoice
      dispatch(setSalesOrderId(salesOrderData?.id))
      dispatch(
        setConsumerInfo({
          salesOrder: salesOrderData?.salesOrderTicketId,
          name: {
            id: salesOrderData?.customer?.id,
            customerCommonIdentifierString: salesOrderData?.customer?.displayName,
          },
        }),
      )
      dispatch(
        setSalesOrderFilter({
          filter: 'customer',
          value: salesOrderData?.customer?.displayName,
        }),
      )
      reset({
        customer: {
          name: {
            id: salesOrderData.customer?.id,
            customerCommonIdentifierString: salesOrderData.customer?.displayName || '',
          },
          salesOrder: salesOrderData.salesOrderTicketId || '',
        },
        details: {
          invoiceNo: invoiceData?.invoiceTicketId || '',
          paymentMethod:
            selectedInboundTickets.length > 0
              ? customerData?.preferredPaymentMethod?.toLowerCase() || ''
              : salesOrderData?.paymentMethod || '',
          invoiceWeights: salesOrderData.orderSalesDetails?.invoiceWeights || '',
          paymentTermId: salesOrderData.paymentTermId || '',
          currency: salesOrderData.currency || defaultCurrency || '',
          issueDate: invoiceData?.details?.issueDate ?? new Date(),
          dateDue: dueDateCalculation(invoiceData?.details?.issueDate ?? new Date(), salesOrderData.paymentTermId),
          notes: invoiceData?.notes || '',
          customForeignExchangeRateEnabled: invoiceData?.customForeignExchangeRateEnabled || false,
          foreignExchangeRate: invoiceData?.foreignExchangeRate || null,
        },
        // reset consumerWeights with itself to prevent it from getting cleared if this useEffect reruns (eg. due to a page error)
        consumerWeights: getValues('consumerWeights') || [],
      })
      initialRender.current = false
    }

    if (isUpdate) {
      setWeightMode(invoiceData?.usingConsumerWeights ? INVOICE_WEIGHT_MODES.CONSUMER : INVOICE_WEIGHT_MODES.YARD)

      // setup initial form data for a new ticket WITHOUT a outboundTicketId
    } else if (initialRender.current) {
      reset(initialInvoiceInformation(defaultCurrency))
      initialRender.current = false
      // handles updating the form when the sales order is changed
    } else {
      setSelectedOutboundTickets([])
      setSelectedInboundTickets([])
      if (salesOrderId) {
        dispatch(
          setSalesOrderFilter({
            filter: 'customer',
            value: salesOrderData?.customer?.displayName,
          }),
        )
      }
      const currentFormData = getValues()
      const currentCustomerName = currentFormData?.customer?.name?.customerCommonIdentifierString
      reset({
        customer: {
          name: {
            id: salesOrderId ? salesOrderData?.customer?.id || '' : currentFormData?.customer?.id,
            customerCommonIdentifierString: salesOrderId
              ? salesOrderData?.customer?.displayName || ''
              : currentCustomerName || '',
          },
          salesOrder: salesOrderId ? salesOrderData?.salesOrderTicketId || '' : '',
        },
        details: {
          invoiceNo: currentFormData.details.invoiceNo,
          paymentMethod:
            selectedInboundTickets.length > 0
              ? customerData?.preferredPaymentMethod?.toLowerCase() || ''
              : salesOrderData?.paymentMethod || '',
          invoiceWeights: currentFormData.details.invoiceWeights,
          paymentTermId: currentFormData.details.paymentTermId,
          currency: salesOrderId ? salesOrderData?.currency || '' : defaultCurrency,
          issueDate: currentFormData.details.issueDate,
          dateDue: salesOrderId
            ? dueDateCalculation(currentFormData.details.issueDate, salesOrderData?.paymentTermId)
            : dueDateCalculation(currentFormData.details.issueDate, currentFormData.details.paymentTermId),
          notes: '',
        },
        consumerWeights: [],
      })
    }
  }, [invoiceData, salesOrderData])

  useEffect(() => {
    setValue('customer.salesOrder', (!!salesOrderId && salesOrderData?.salesOrderTicketId) || '')
    if (!isUpdate && !salesOrderId) {
      setSelectedOutboundTickets([])
    }
  }, [salesOrderId, salesOrderData])

  useEffect(() => {
    if (consumerInfo?.consumer?.customerCommonIdentifierString) {
      setValue('customer.name', consumerInfo?.consumer)
    } else {
      setValue('customer.name', consumerInfo?.name)
    }
  }, [consumerInfo])

  useEffect(() => {
    if (outboundTicketData) {
      dispatch(clearOutboundTicketId())
      setSelectedOutboundTickets([outboundTicketData])
    }
  }, [outboundTicketData])

  useEffect(() => {
    if (inboundTicketData) {
      setSelectedInboundTickets([inboundTicketData])

      // Set customer information from inbound ticket data
      if (inboundTicketData.customer) {
        const customerInfo = {
          name: {
            id: inboundTicketData.customer.id,
            customerCommonIdentifierString:
              inboundTicketData.customer.displayName || inboundTicketData.customer.customerCommonIdentifierString,
          },
          salesOrder: '', // Inbound tickets don't have sales orders
        }
        dispatch(setConsumerInfo(customerInfo))
        setValue('customer.name', customerInfo.name)
        setValue('customer.salesOrder', '')
      }
    }
  }, [inboundTicketData])

  // Second effect: Handle customer-specific data after it's loaded
  useEffect(() => {
    if (inboundTicketData?.customer && customerData && !isUpdate) {
      // Set default payment method for inbound tickets
      const defaultPaymentMethod = customerData?.preferredPaymentMethod?.toLowerCase() || yardPreferredPaymentMethod
      setValue('details.paymentMethod', defaultPaymentMethod)
    }
  }, [customerData, inboundTicketData, isUpdate])

  useEffect(() => {
    let paymentTerm = salesOrderId === salesOrderData?.id ? salesOrderData?.paymentTermId : null
    paymentTerm = paymentTerm ?? getValues('details.paymentTermId')
    setValue('details.dateDue', dueDateCalculation(issueDate || new Date(), paymentTerm))
  }, [issueDate, selectedPaymentTermId, salesOrderId, salesOrderData])

  useEffect(() => {
    if (outboundTicketsData) {
      setOutboundTicketsList(
        outboundTicketsData?.Items.filter(item => {
          const notVoid = item.status !== 'VOID'
          const isShipped = item.status === 'SHIPPED'
          const hasInvoice = !!item?.invoiceId
          const onCurrentInvoice = item?.invoiceId === invoiceData?.id
          const hasSalesOrder = !!item?.contractInfo?.salesOrder
          const matchesSalesOrder = salesOrderId
            ? parseInt(item.contractInfo?.salesOrder, 10) === salesOrderData?.salesOrderTicketId
            : true
          return notVoid && ((!hasInvoice && isShipped) || onCurrentInvoice) && hasSalesOrder && matchesSalesOrder
        })
          .sort((a, b) => Number(new Date(b.effectiveDate || b.date)) - Number(new Date(a.effectiveDate || a.date)))
          .map(item => ({
            id: item.id,
            ticketId: Number(item.tag) || item.tag,
            millScaleNumber: item.millScaleNumber,
            date: item.effectiveDate
              ? dateTimeFormatter(item.effectiveDate, { timeZone: defaultYardTimezone })
              : dateFormatter(item.date),
            customer: item.customerDisplayName,
            salesOrderId: item.contractInfo.salesOrder,
            materials: item.materialTypeCodes.map(code => ({ materialName: code })),
            status: capitalize(item.status),
            netWeight: item.netWeight,
            value: '',
            userId: item.userId,
          })),
      )
    }
  }, [salesOrderData, outboundTicketsData, invoiceData])

  const showInvoiceNumberTip = !isLoading && !watch('details.invoiceNo') && invoiceNumTouched

  const onAddOutboundTickets = useCallback(
    ids => {
      setIsFetchingOutboundTickets(true)
      fetchTickets(ids)
      setOpenAddOutboundTickets(false)

      const newTotalRowData = {}
      ids.forEach(id => {
        newTotalRowData[id] = initialInvoiceSummaryRowData
      })
      setTotalRowData(newTotalRowData)
    },
    [outboundTicketsList],
  )

  // update totalRowData, which stores total row data for each OBT
  const updateTotalRowData = useCallback((id, payload) => {
    const currentTotalRowData = totalRowDataRef?.current?.[id] || {}
    const { grossWeight, tareWeight, grossValue, tareValue, freightValue } = currentTotalRowData

    const shouldUpdate =
      grossWeight !== payload.grossWeight ||
      tareWeight !== payload.tareWeight ||
      grossValue !== payload.grossValue ||
      tareValue !== payload.tareValue ||
      freightValue !== payload.freightValue

    if (shouldUpdate) {
      setTotalRowData(previousTotalRowData => ({
        ...previousTotalRowData,
        [id]: payload,
      }))
    }
  }, [])

  // update invoice summary data whenever totalRowData is updated
  useEffect(
    function updateSummaryData() {
      // Set summary data from API response if we are not in edit mode
      if (invoiceData && !isEditMode && selectedInboundTickets.length === 0) {
        const usingConsumerWeights = invoiceData.usingConsumerWeights
        const grossWeight = usingConsumerWeights
          ? invoiceData.consumerSummary.grossTotal
          : invoiceData.yardSummary.grossTotal
        const tareWeight = usingConsumerWeights
          ? invoiceData.consumerSummary.tareTotal
          : invoiceData.yardSummary.tareTotal
        const netWeight = usingConsumerWeights ? invoiceData.consumerSummary.netTotal : invoiceData.yardSummary.netTotal
        const subTotal = usingConsumerWeights ? invoiceData.consumerSummary.subtotal : invoiceData.yardSummary.subtotal
        const taxValue = usingConsumerWeights ? invoiceData.consumerSummary.tax : invoiceData.yardSummary.tax
        const totalValue = usingConsumerWeights ? invoiceData.consumerSummary.total : invoiceData.yardSummary.total
        const freightValue = invoiceData.freight
        setSummaryData({
          grossWeight,
          tareWeight,
          netWeight,
          subTotal,
          freightValue,
          taxValue,
          totalValue,
        })
      } else {
        // calculate the summary data if we are editing
        const effectiveTaxRate = (invoiceRate, customerRate, billRate) => {
          if (Number.isFinite(parseFloat(invoiceRate))) return invoiceRate
          if (Number.isFinite(parseFloat(customerRate))) return customerRate
          if (Number.isFinite(parseFloat(billRate))) return billRate
          return 0.0
        }

        // For inbound invoices, use billTaxRate; for outbound invoices, use invoice/customer tax rate
        const taxRate =
          selectedInboundTickets.length > 0
            ? billTaxRate
            : effectiveTaxRate(invoiceData?.taxRate, customerData?.taxRate, billTaxRate)
        const newSummaryData = Object.keys(totalRowData).reduce((aggregate, key) => {
          const { grossWeight, tareWeight, grossValue, tareValue, freightValue } = totalRowData[key]
          const newAggregate = {
            grossWeight: aggregate.grossWeight + grossWeight,
            tareWeight: aggregate.tareWeight + tareWeight,
            grossValue: aggregate.grossValue + grossValue,
            tareValue: aggregate.tareValue + tareValue,
            freightValue: aggregate.freightValue + freightValue,
          }

          const subTotal = Big(newAggregate.grossValue).minus(newAggregate.tareValue).round(2)
          const taxValue = subTotal.times(taxRate).div(100.0).round(2)
          const totalValue = subTotal.plus(taxValue).round(2)
          return {
            ...newAggregate,
            taxValue,
            subTotal,
            totalValue,
          }
        }, initialInvoiceSummaryRowData)

        setSummaryData({
          ...newSummaryData,
          grossValue: parseFloat(newSummaryData.grossValue.toFixed(2)),
          tareValue: parseFloat(newSummaryData.tareValue.toFixed(2)),
        })
      }
    },
    [invoiceData, totalRowData, billTaxRate, selectedInboundTickets, customerData],
  )

  const renderTickets = () => {
    if (isLoading) return null

    // For new invoices or when creating invoices
    if (selectedInboundTickets.length > 0) {
      return selectedInboundTickets.map(ticket => (
        <InvoiceTicketTableV2
          key={ticket.id}
          ticketInfo={ticket}
          ticketType={TICKET_SOURCE_TYPE.INBOUND}
          updateTotalRowData={updateTotalRowData}
          invoiceData={invoiceData}
          salesOrderData={salesOrderData}
          purchaseOrderData={purchaseOrderData}
          setTicketsAreLoading={setTicketsAreLoading}
          weightMode={weightMode}
          isEditMode={!isUpdate || isEditMode}
          control={control}
          setValue={setValue}
          pricePrepend={pricePrepend}
          setCachedMaterialTypesData={setCachedMaterialTypesData}
          apiRefDict={apiRefDict}
          setApiRefDict={setApiRefDict}
          setOpenDeduction={setOpenDeduction}
          setOnSaveDeductionHandler={setOnSaveDeductionHandlerState}
          setSelectedDeductions={setSelectedDeductions}
        />
      ))
    }

    // Fallback to outbound tickets
    if (selectedOutboundTickets.length > 0) {
      return selectedOutboundTickets.map(ticket => (
        <InvoiceTicketTableV2
          key={ticket.id}
          ticketInfo={ticket}
          ticketType={TICKET_SOURCE_TYPE.OUTBOUND}
          updateTotalRowData={updateTotalRowData}
          invoiceData={invoiceData}
          salesOrderData={salesOrderData}
          setTicketsAreLoading={setTicketsAreLoading}
          weightMode={weightMode}
          isEditMode={!isUpdate || isEditMode}
          control={control}
          setValue={setValue}
          pricePrepend={pricePrepend}
          setCachedMaterialTypesData={setCachedMaterialTypesData}
          apiRefDict={apiRefDict}
          setApiRefDict={setApiRefDict}
          setOpenDeduction={setOpenDeduction}
          setOnSaveDeductionHandler={setOnSaveDeductionHandlerState}
          setSelectedDeductions={setSelectedDeductions}
        />
      ))
    }

    return null
  }

  return (
    <FormProvider {...methods}>
      <form>
        <Stack minHeight="calc(100vh - 235px)" p={3} spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Box width={{ xs: '100%', md: '40%' }}>
              <Typography variant="h6" gutterBottom color="textSecondary">
                Customer Information
              </Typography>
              <Stack
                direction="row"
                padding={2}
                spacing={2}
                sx={{
                  backgroundColor: 'background.section',
                  borderRadius: '8px',
                }}
              >
                <CustomerAutocomplete
                  name="customer.name"
                  label="Customer Name"
                  data-test="customer-name"
                  variant="outlined"
                  size="small"
                  disabled={isUpdate || selectedInboundTickets.length > 0}
                  defaultValue={{}}
                  control={control}
                  error={!!errors?.customer?.name}
                  helperText={errors?.customer?.name?.message}
                  freeSolo={false}
                  onChange={customer => {
                    dispatch(clearSalesOrderId())
                    setSelectedOutboundTickets([])
                    setSelectedInboundTickets([])
                    dispatch(clearConsumerInformation())
                    dispatch(clearOutboundTicketId())
                    dispatch(clearInboundTicketId())
                    if (customer) {
                      dispatch(
                        setSalesOrderFilter({
                          filter: 'customer',
                          value: customer.displayName,
                        }),
                      )
                    } else {
                      dispatch(clearSalesOrderFilters())
                    }
                  }}
                  autocompleteProps={{
                    renderOption: (props, option) => (
                      <li {...props} key={option.id}>
                        {option.displayName}
                      </li>
                    ),
                  }}
                />
                {selectedInboundTickets.length === 0 && (
                  <Input
                    name="customer.salesOrder"
                    label="Sales Order"
                    data-test="customer-salesOrder"
                    variant="outlined"
                    size="small"
                    disabled={isUpdate}
                    defaultValue=""
                    control={control}
                    error={!!errors.customerSalesOrder}
                    helperText={errors.customerSalesOrder?.message}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            disabled={isUpdate}
                            onClick={() =>
                              dispatch(
                                showFormModal({
                                  formModalName: 'link-sales-order',
                                  formModalProps: {
                                    disableStatusFilter: true,
                                  },
                                }),
                              )
                            }
                          >
                            <ManageSearchIcon color={isUpdate ? undefined : 'primary'} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Stack>
            </Box>
            <Box width={{ xs: '100%', md: '60%' }}>
              <Typography variant="h6" gutterBottom color="textSecondary">
                Details
              </Typography>
              <Stack
                padding={2}
                spacing={2}
                sx={{
                  backgroundColor: 'background.section',
                  borderRadius: '8px',
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Tooltip
                    title="An anto-generated number will be applied if this field is cleared"
                    placement="top"
                    disableHoverListener
                    open={showInvoiceNumberTip}
                  >
                    <div style={{ width: '100%' }}>
                      <Input
                        name="details.invoiceNo"
                        label="Invoice #"
                        data-test="details-invoiceNo"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        onBlur={e => {
                          setInvoiceNumTouched(true)
                        }}
                        error={!!errors.details?.invoiceNo}
                        onChange={() => {
                          clearErrors('details.invoiceNo')
                          setIsInvoiceNumberValid(true)
                        }}
                        helperText={errors.details?.invoiceNo?.message}
                        disabled={isUpdate}
                        InputProps={{
                          inputComponent: InputFormat,
                          inputProps: {
                            name: 'details.invoiceNo',
                            inputType: 'number',
                            onChange: () => {},
                            thousandSeparator: false,
                            decimalScale: 0,
                          },
                        }}
                      />
                    </div>
                  </Tooltip>
                  <Select
                    name="details.currency"
                    label="Currency"
                    data-test="details-currency"
                    size="small"
                    defaultValue={defaultCurrency}
                    control={control}
                    items={CURRENCY_OPTIONS}
                    disabled
                    InputLabelProps={{ shrink: true }}
                  />
                  <Select
                    name="details.paymentMethod"
                    label="Payment Method"
                    data-test="details-paymentMethod"
                    size="small"
                    defaultValue=""
                    control={control}
                    items={PAYMENT_METHOD_OPTIONS}
                    disabled={
                      selectedInboundTickets.length === 0 ||
                      (selectedInboundTickets.length > 0 && isUpdate && !isEditMode)
                    }
                  />
                  {selectedInboundTickets.length === 0 && (
                    <Select
                      name="details.invoiceWeights"
                      label="Invoice Weights"
                      data-test="details-invoiceWeights"
                      size="small"
                      defaultValue=""
                      control={control}
                      items={WEIGHT_OPTIONS}
                      disabled
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={2} mt={2}>
                  <Select
                    name="details.paymentTermId"
                    label="Payment Terms"
                    data-test="details-paymentTerm"
                    size="small"
                    defaultValue=""
                    control={control}
                    items={paymentTermsOptions}
                    disabled={
                      selectedInboundTickets.length === 0 ||
                      (selectedInboundTickets.length > 0 && isUpdate && !isEditMode)
                    }
                  />
                  <DatePicker
                    name="details.issueDate"
                    label="Issue Date"
                    data-test="details-issueDate"
                    variant="outlined"
                    size="small"
                    datePickerProps={{
                      disabled: isUpdate && !isEditMode,
                      minDate:
                        lockAccountingPeriod.isLockedForAccounting && lockAccountingPeriod.lockAccountingDate
                          ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                          : undefined,
                    }}
                    error={!!errors.issueDate}
                    helperText={errors.issueDate?.message}
                    control={control}
                    defaultDate={new Date()}
                    InputProps={isUpdate && !isEditMode ? { endAdornment: null } : {}}
                    InputLabelProps={{ shrink: true }}
                  />
                  <DatePicker
                    name="details.dateDue"
                    label="Date Due"
                    data-test="details-dateDue"
                    variant="outlined"
                    size="small"
                    datePickerProps={{
                      disabled: true,
                    }}
                    error={!!errors.dateDue}
                    helperText={errors.dateDue?.message}
                    control={control}
                    defaultDate={new Date()}
                    InputProps={
                      selectedInboundTickets.length === 0 ||
                      (selectedInboundTickets.length > 0 && isUpdate && !isEditMode)
                        ? { endAdornment: null }
                        : {}
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
                {showCustomFxRate && (
                  <Stack direction="row" spacing={2} mt={2}>
                    <Switch
                      name="details.customForeignExchangeRateEnabled"
                      label="Custom FX Rate"
                      data-test="details-custom-foreign-exchange-rate-enabled"
                      control={control}
                      labelPlacement="start"
                      disabled={!isEditMode}
                      hidden={!showCustomFxRate}
                      sx={{ minWidth: '150px' }}
                    />
                    <Input
                      name="details.foreignExchangeRate"
                      label="Exchange Rate"
                      data-test="details-foreign-exchange-rate"
                      variant="outlined"
                      size="small"
                      control={control}
                      disabled={!customForeignExchangeRateEnabled || !isEditMode}
                      sx={{ flex: 1 }}
                      InputProps={{
                        inputComponent: InputFormat,
                        inputProps: {
                          inputType: 'number',
                          decimalScale: 4,
                          fixedDecimalScale: true,
                          allowNegative: false,
                        },
                      }}
                    />
                  </Stack>
                )}
              </Stack>
            </Box>
          </Stack>
          {invoiceHasPayments ? (
            <Stack>
              <Typography variant="h6" gutterBottom color="textSecondary">
                Payments Received
              </Typography>
              <InvoicePaymentTableV2
                invoiceId={invoiceData?.id}
                paymentInfo={invoiceData?.paymentSplits}
                loading={isFetchingInvoice}
                pricePrepend={pricePrepend}
                handleOpenBulkPayment={handleOpenBulkPayment}
              />
            </Stack>
          ) : null}
          <Stack>
            <Typography variant="h6" color="textSecondary">
              {selectedInboundTickets.length > 0 ? 'Inbound Tickets' : 'Shipment Tickets'}
            </Typography>
            {!isUpdate || isEditMode ? (
              <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  {selectedInboundTickets.length === 0 && (
                    <Button
                      data-test="add-tickets"
                      variant={selectedOutboundTickets.length > 0 ? 'outlined' : 'contained'}
                      disabled={
                        !getValues()?.customer?.name?.customerCommonIdentifierString ||
                        (selectedOutboundTickets.length > 0 && !getValues()?.customer?.salesOrder)
                      }
                      onClick={() => {
                        setOpenAddOutboundTickets(true)
                      }}
                    >
                      {selectedOutboundTickets.length > 0 ? 'ADD/REMOVE TICKETS' : 'ADD TICKETS'}
                    </Button>
                  )}
                </Box>
                {selectedInboundTickets.length === 0 && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">Weights by:</Typography>
                    <ToggleButtonGroup
                      value={weightMode}
                      exclusive
                      onChange={(_, mode) => {
                        if (!mode) return
                        setWeightMode(mode)
                      }}
                    >
                      <ToggleButton size="small" sx={{ textTransform: 'none' }} value={INVOICE_WEIGHT_MODES.YARD}>
                        Yard Weights
                      </ToggleButton>
                      <ToggleButton size="small" sx={{ textTransform: 'none' }} value={INVOICE_WEIGHT_MODES.CONSUMER}>
                        Consumer Weights
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}
              </Box>
            ) : null}
            {renderTickets()}
          </Stack>
        </Stack>
        <AdjustmentModal
          title="Adjustments"
          adjustments={selectedDeductions}
          open={openDeduction}
          onSave={deductions => onSaveDeductionHandler(deductions)}
          onClose={() => setOpenDeduction(false)}
        />
        <OutboundTicketsModal
          title="Import Tickets to Invoice"
          buttonTitle={selectedOutboundTickets.length > 0 ? 'UPDATE' : 'IMPORT TICKETS'}
          outboundTicketsList={outboundTicketsList}
          selectedOutboundTicketIds={selectedOutboundTickets.map(ticket => ticket.id)}
          open={openAddOutboundTickets}
          onAdd={onAddOutboundTickets}
          onClose={() => setOpenAddOutboundTickets(false)}
          onSave={() => {}}
          pricePrepend={pricePrepend}
        />
        {supportVoidInvoice && (
          <AlertModal
            open={showVoidModal}
            modalType="void"
            title="Void This Invoice"
            question="Are you sure you want to void this invoice?"
            description="All tickets attached to this invoice will be returned to their previous status"
            confirmLabel="YES, VOID THIS INVOICE"
            cancelLabel="GO BACK"
            onClose={() => {
              setShowVoidModal(false)
            }}
            data-testid="void-invoice-modal"
            onSubmit={onClickVoidInvoice}
          />
        )}
        {supportVoidInvoice && (
          <AlertSnackbar
            message={voidInvoiceErrorMsg}
            isOpen={!!voidInvoiceErrorMsg}
            severity="error"
            handleClose={() => setVoidInvoiceErrorMsg('')}
          />
        )}
        <EmailModal
          open={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          recipients={customerData?.contacts?.map(contact => contact.email).filter(Boolean)}
          pdfTypeOptions={[PDF_TYPE.INVOICE]}
          defaultPdfType={PDF_TYPE.INVOICE}
          entityId={invoiceData?.id}
        />
        {selectedOutboundTickets.length > 0 || selectedInboundTickets.length > 0 ? (
          <InvoiceFooter summaryData={summaryData} canEditNotes={!isUpdate || isEditMode} pricePrepend={pricePrepend} />
        ) : null}
      </form>
    </FormProvider>
  )
}

export default InvoiceContainerV2
